import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  linkedSignal,
  viewChild,
} from '@angular/core';
import { MibBreakpointService, MibButtonDirective, watchSize } from '@medinbox/angular';
import { PromptExample } from '@shared/models';
import { ChatService } from '../chat.service';
import { PromptBox } from '../prompt-box';
import { PromptExampleIconComponent } from '../prompt-examples-dropdown/prompt-example-icon';

@Component({
  selector: 'mai-chat-empty-screen',
  imports: [MibButtonDirective, PromptBox, PromptExampleIconComponent],
  templateUrl: './chat-empty-screen.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatEmptyScreen {
  private readonly service = inject(ChatService);
  private readonly breakpointService = inject(MibBreakpointService);
  private readonly scroller = viewChild.required<ElementRef<HTMLElement>>('scroller');
  private readonly content = viewChild.required<ElementRef<HTMLElement>>('content');
  private readonly examples = viewChild<ElementRef<HTMLElement>>('examples');
  private fullWelcomeHeight = 0;

  readonly promptBox = viewChild.required(PromptBox);

  readonly assistantIntro = this.service.assistantIntro;
  readonly assistantSubtext = this.service.assistantSubtext;
  readonly promptExamples = this.service.promptExamples;
  readonly isCompactHeight = computed(() => this.breakpointService.heightClass() === 'compact');
  /** Tighter gaps and a wider block; new examples are measured in the full welcome first. */
  protected readonly compactWelcome = linkedSignal({
    source: this.promptExamples,
    computation: () => false,
  });
  /** Only in the compact welcome, and only where two per row is shorter than one. */
  protected readonly twoPerRow = linkedSignal({
    source: this.promptExamples,
    computation: () => false,
  });

  constructor() {
    afterRenderEffect((onCleanup) => {
      const scroller = this.scroller().nativeElement;
      const content = this.content().nativeElement;
      const fit = (): void => this.fitWelcome(scroller, content);
      onCleanup(watchSize(scroller, fit));
      onCleanup(watchSize(content, fit));
    });
  }

  useExample(example: PromptExample): void {
    this.promptBox().setPrompt(example.prompt);
  }

  /** Compact as soon as the full welcome would come within the padding of the edges. */
  private fitWelcome(scroller: HTMLElement, content: HTMLElement): void {
    // The compact welcome is shorter: compare the full height kept from before, or it would flip back.
    if (!this.compactWelcome()) {
      const { paddingTop, paddingBottom } = getComputedStyle(scroller);
      const padding = parseFloat(paddingTop) + parseFloat(paddingBottom);
      this.fullWelcomeHeight = content.offsetHeight + 2 * padding;
    }
    const canCompact = this.breakpointService.sizeClass() === 'expanded' && !this.isCompactHeight();
    const compact = canCompact && this.fullWelcomeHeight > scroller.clientHeight;
    this.compactWelcome.set(compact);
    this.twoPerRow.set(compact && this.twoPerRowIsShorter(content));
  }

  /** Lays the examples out both ways within one task, so neither is ever painted. */
  private twoPerRowIsShorter(content: HTMLElement): boolean {
    const grid = this.examples()?.nativeElement;
    if (!grid) {
      return false;
    }
    grid.style.gridTemplateColumns = 'minmax(0, 1fr)';
    const oneColumn = content.offsetHeight;
    grid.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
    const twoColumns = content.offsetHeight;
    grid.style.gridTemplateColumns = '';
    return twoColumns < oneColumn;
  }
}
